import React, { useState, useRef, useEffect } from 'react';
import { XMarkIcon } from 'components/icons';

interface Suggestion {
  value: string;
  label: string;
}

interface TagInputProps {
  name: string;
  value: string[];
  onChange: (tags: string[]) => void;
  suggestions?: Suggestion[];
  placeholder?: string;
  helpText?: string;
  label?: string;
}

const TagInput: React.FC<TagInputProps> = ({
  name,
  value,
  onChange,
  suggestions = [],
  placeholder = 'Type to search or add...',
  helpText,
  label,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter suggestions based on input and exclude already-selected
  const filtered = inputValue.trim().length > 0
    ? suggestions.filter(s =>
        (s.value.toLowerCase().includes(inputValue.toLowerCase()) ||
         s.label.toLowerCase().includes(inputValue.toLowerCase())) &&
        !value.includes(s.value)
      ).slice(0, 8)
    : [];

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
          inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setInputValue('');
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  const removeTag = (tag: string) => {
    onChange(value.filter(t => t !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (highlightedIndex >= 0 && filtered[highlightedIndex]) {
        addTag(filtered[highlightedIndex].value);
      } else if (inputValue.trim()) {
        addTag(inputValue);
      }
    } else if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
      removeTag(value[value.length - 1]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <div>
      {label && <label className="label">{label}</label>}
      {helpText && <p className="text-xs text-content-muted mb-1">{helpText}</p>}

      {/* Hidden input for form submission */}
      <input type="hidden" name={name} value={value.join(', ')} />

      {/* Tags + Input Container */}
      <div
        className="flex flex-wrap gap-1.5 p-2 border border-border-strong rounded-lg bg-surface min-h-[42px] cursor-text focus-within:ring-2 focus-within:ring-brand-500 focus-within:border-brand-500"
        onClick={() => inputRef.current?.focus()}
      >
        {value.map(tag => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 bg-brand-100 text-brand-700 px-2 py-0.5 rounded-md text-sm"
          >
            {tag}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeTag(tag); }}
              className="hover:text-brand-900"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
        <div className="relative flex-1 min-w-[120px]">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowSuggestions(true);
              setHighlightedIndex(-1);
            }}
            onFocus={() => { if (inputValue.trim()) setShowSuggestions(true); }}
            onKeyDown={handleKeyDown}
            placeholder={value.length === 0 ? placeholder : 'Add more...'}
            className="w-full border-none outline-none bg-transparent text-sm py-0.5 px-1"
          />

          {/* Suggestions Dropdown */}
          {showSuggestions && filtered.length > 0 && (
            <div
              ref={dropdownRef}
              className="absolute left-0 top-full mt-1 w-72 max-h-48 overflow-y-auto bg-surface border border-border rounded-lg shadow-lg z-50"
            >
              {filtered.map((suggestion, idx) => (
                <button
                  key={suggestion.value}
                  type="button"
                  onClick={() => addTag(suggestion.value)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-surface-sunk ${
                    idx === highlightedIndex ? 'bg-brand-50 text-brand-700' : 'text-content'
                  }`}
                >
                  <span className="font-medium">{suggestion.value}</span>
                  <span className="text-content-subtle ml-2">{suggestion.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TagInput;
